import { AfterViewInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  InterruptHostRef,
  PostLoginInterrupt,
} from '../../../core/services/post-login-interrupt/post-login-interrupt.model';

/** Sentinel index for the cover — slide zero is the first real item. */
const COVER = -1;

/**
 * INC-328 — the post-login interrupt frame. Design variant A3, "cover page then deck",
 * settled by prototype and LOCKED; the reasoning, the research and the measured numbers
 * are on INC-328 and are not re-derived here.
 *
 * PRECONDITION: only ever opened with TWO OR MORE interrupts. With one, the queue opens
 * that body as its own modal instead, so "one pending item ⇒ no cover and no frame
 * chrome at all" is structural rather than a branch in here.
 *
 * The two hosted bodies are reused unforked, rendered through `ngComponentOutlet` and
 * handed an `InterruptHostRef` so their buttons resolve ONE ITEM rather than tearing the
 * frame down — no stub `NgbActiveModal`, and no `::ng-deep` to hide the vote card's own
 * header, which now suppresses itself through `hostOwnsChrome`.
 *
 * Purely input-driven: it takes the items and a way to make host refs, and knows nothing
 * about the queue that opened it.
 */
@Component({
  selector: 'app-interrupt-frame',
  templateUrl: './interrupt-frame.component.html',
  styleUrls: ['./interrupt-frame.component.scss'],
  standalone: false,
})
export class InterruptFrameComponent implements AfterViewInit {
  @Input() items: PostLoginInterrupt[] = [];

  /** Supplied by the queue: where a hosted body's outcome goes. */
  @Input() hostRefFor: (interrupt: PostLoginInterrupt) => InterruptHostRef;

  /** Focus target on open and on every view change (APG dialog pattern). */
  @ViewChild('heading') heading?: ElementRef<HTMLElement>;
  @ViewChild('cardRegion') cardRegion?: ElementRef<HTMLElement>;

  index = COVER;

  /**
   * Height floor for the card region, in px, grown to the tallest card seen in this
   * queue so paging never resizes the frame.
   *
   * Measured at runtime rather than hard-coded: the two cards differ by ~120px at 375px
   * wide, and a constant would be a guess that rots the moment copy or a breakpoint
   * changes. The shorter card is centred in the box so the slack reads as padding.
   */
  cardFloor = 0;

  constructor(private readonly activeModal: NgbActiveModal) {}

  ngAfterViewInit(): void {
    this.focusHeading();
    this.growCardFloor();
  }

  get onCover(): boolean {
    return this.index === COVER;
  }

  get active(): PostLoginInterrupt {
    return this.items[Math.max(0, this.index)];
  }

  /**
   * The compliance gate still queued, if any. Drives the note that keeps the gate
   * legible on every card view — including, above all, the views where its own card is
   * not the one on screen.
   */
  get gate(): PostLoginInterrupt | undefined {
    return this.items.find((item) => item.kind === 'gate');
  }

  /**
   * The gate with no defer path at all, if one is queued — distinct from {@link gate}, which
   * also matches the deferable profile-refresh nudge. Only this
   * one disables the frame's × (see {@link close}).
   */
  get mandatoryGate(): PostLoginInterrupt | undefined {
    return this.items.find((item) => item.mandatory);
  }

  /**
   * The gate's label, HTML-escaped for the one place it reaches `[innerHTML]`.
   *
   * `gateNoteElsewhere` has to carry its own `<strong>` — splitting the sentence into
   * spans would fix the word order in English and break it in ms/zh — so the note is
   * bound with `[innerHTML]`, and ngx-translate does NOT escape what it interpolates.
   * Today every label is developer-supplied copy, so nothing hostile can reach here; the
   * escape is what keeps that true when a future interrupt labels itself from the server.
   * Same treatment as `escapedEmail` on the unregistered-submission modal.
   */
  get escapedGateLabel(): string {
    return (this.gate?.label ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  goTo(index: number): void {
    this.index = index;
    this.focusHeading();
    this.growCardFloor();
  }

  backToCover(): void {
    this.index = COVER;
    this.focusHeading();
  }

  page(delta: number): void {
    const count = this.items.length;
    this.goTo((this.index + delta + count) % count);
  }

  /**
   * × — the investor closed the frame with items still on it. The queue defers each.
   *
   * A no-op while a MANDATORY gate is queued (see {@link mandatoryGate}): the single-
   * interrupt path already refuses Esc/backdrop dismissal for that item via its own
   * `modalOptions` ("the only way out is accepting"), and the frame's × must not become a
   * laxer escape hatch just because a dismissible item happens to be queued alongside it.
   * `openFrame` pairs this with `backdrop: 'static'` / `keyboard: false` on the modal itself
   * so Esc/backdrop-click are blocked the same way. The template also hides the × entirely
   * while this applies, so it is never shown as a control that silently does nothing.
   * A merely deferable gate (e.g. profile-refresh) still closes normally — its own "Remind
   * me later" path must survive being framed with another interrupt.
   */
  close(): void {
    if (this.mandatoryGate) {
      return;
    }
    this.activeModal.dismiss();
  }

  /**
   * Inputs for a hosted body, built once per item: change detection re-reads this on
   * every pass, and a fresh object each time would re-set the inputs endlessly.
   */
  inputsFor(interrupt: PostLoginInterrupt): Record<string, unknown> {
    let inputs = this.hostInputs.get(interrupt.key);
    if (!inputs) {
      inputs = {
        ...(interrupt.inputs ?? {}),
        host: this.hostRefFor(interrupt),
        hostOwnsChrome: true,
      };
      this.hostInputs.set(interrupt.key, inputs);
    }
    return inputs;
  }

  private readonly hostInputs = new Map<string, Record<string, unknown>>();

  /**
   * APG: focus must not be left on a control the view change just destroyed. Parking it
   * on the new heading also announces where the investor has landed.
   */
  private focusHeading(): void {
    setTimeout(() => this.heading?.nativeElement?.focus());
  }

  /** Raise the floor to the tallest card rendered so far. Never lowers it. */
  private growCardFloor(): void {
    setTimeout(() => {
      const element = this.cardRegion?.nativeElement;
      if (!element) {
        return;
      }
      // scrollHeight is the natural content height while the floor is not the constraint.
      const natural = element.scrollHeight;
      if (natural > this.cardFloor) {
        this.cardFloor = natural;
      }
    });
  }
}
